/* The Website panel: her pages, and the blocks each page is written in.
 *
 * MomentumHub keeps the state and hands this the props. The screen pattern in
 * CLAUDE.md was worked out here first — nothing to press, the whole row opens,
 * Brill proposes and she locks — so if the two ever disagree, this is the file
 * that moved.
 */
import { useState } from "react";
import { offerForPage } from "../../utils/pageOffer";
import { PAGE_KINDS, SEED_PAGES, contextScopeFor, slugTaken, slugify, sourceSectionFor } from "../../utils/websitePages";
import { hasContent, kindLabel } from "../../utils/websiteSectionKinds";
import { SECTION_DONE, pageState, pageWords, sectionHeading, sourceChangedFor, startSection } from "../../utils/websiteSections";
import { SectionStatus } from "./fields";
import { SortableItem, SortableList } from "../BrowseTable";
import { siteKindDef } from "../../utils/useSites";
/* The blocks themselves live in sections.jsx — a funnel page-step renders the
   same ones. What stays here is what only a WEBSITE page has. */
import { AddSectionMenu, SectionBlock, SectionDrafter } from "./sections";
import { B, STATUS_DOT, btnGhost, input, nb, pDesc, pTitle, pTitleAlone } from "./tokens";
import { AddButton, Back, Badge, Bread, Hint, IconBtn, Menu2, Note, PageTitleField, StateDots, useTwoPane } from "./ui";
import { rungTag } from "../../utils/offerRung";
/* The bar, the rail and the footer are the same on every panel — shell.jsx. */
import { BarBack, L1Table, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { CopySiteBrief } from "./copyBrief";
import { siteHref } from "../../utils/siteHref";
import { SITE_STYLES, styleDef } from "../../utils/siteStyles";
import { readWorthi, worthiLabel } from "../../utils/worthi";
import { ChevronDown, ExternalLink, Settings, Trash2 } from "lucide-react";



/* What each standard page is for is on the kind itself (`PAGE_KINDS`), written
   for someone who hasn't built a site before — "Services" alone doesn't say
   whether that's one page or one per offer. Read from there rather than kept in a
   second map here, which is how the menu ends up describing a page differently
   from the page. */
export const pageHint = (slug) => PAGE_KINDS.find((k) => k.slug === slug)?.hint || "";


/* Add page and the view switch on one row, rendered by both views so the
   controls sit in the same place whichever one she's in — a toggle that moves
   when you use it is a toggle you have to hunt for. Add page lives here rather
   than only on the table because the moment she wants a new page is usually
   while she's writing one.

   The row itself is `MomentumBar` now (shell.jsx) — every panel's bar is the
   same three slots in the same order, and this file's job is only to say what
   goes in the Add one. What stays website-specific is the MENU: the standard
   pages are offered by name because that's faster than typing each one and
   getting the address right, and one she already has is shown disabled rather
   than hidden, so the menu is the same shape every time she opens it. */
export function WebsiteBar({ menuOpen, setMenuOpen, onAddStandard, onAddOther, missing = [], standardPages = SEED_PAGES, lead, level1, level2, drawer, drafting = false }) {
  const missingSlugs = new Set(missing.map((m) => m.slug));
  return (
    <MomentumBar lead={lead} level1={level1} level2={level2} drawer={drawer} drafting={drafting} add={
      <div style={{ position: "relative", display: "inline-block" }}>
        <AddButton label="Add page" onClick={() => setMenuOpen(!menuOpen)} />
        {menuOpen && (
          <Menu2 head="Which page?" items={[
            ...standardPages.map((p) => [
              "📄", p.title, pageHint(p.slug), !missingSlugs.has(p.slug),
              () => { setMenuOpen(false); onAddStandard(p.slug); },
            ]),
            ["✏️", "Other", pageHint(""), false,
              () => { setMenuOpen(false); onAddOther(); }],
          ]} />
        )}
      </div>
    } />
  );
}




/* Same consolidation as Courses (see COURSE_HINT there, and rule 9). Kept only
   what the screen cannot show her: the BOUNDARY — Brill holds the words and
   the pictures, not the build — and that the address on a page is a label for
   the handoff rather than somewhere Brill publishes to. Said "layout, images
   and templates happen wherever you build the site" until 2026-10-28, which
   pictures, the preview and Copy spec had all made untrue. */
/* Assigned WITHOUT wrapping parens on purpose: `const NAME = (` is the shape
   src/hookOrder.test.js sweeps for, because an arrow-function COMPONENT written
   that way would slip past its hook-order check unseen. This is a JSX value, not
   a component — it is never rendered as <SITE_HINT /> — but the sweep cannot
   know that, and adding it to the allowlist would spend the guard's strictness
   on a constant. Dropping two brackets is cheaper. */
const SITE_HINT = <>Each page written section by section from your locked
    foundation, with your pictures. Brill doesn't build the site: Copy spec
    hands each page to whichever builder you use. The address on a page is a
    label for whoever builds it.</>;


/* The same three words `MODULE_STATUS_LABEL` uses, because a dot has to mean
   one thing on both panels. "Not written" rather than "Empty": a page with four
   blank sections is not empty, it is unwritten, and those are different things
   to see down a list. */
export const PAGE_STATE_LABEL = { done: "Done", draft: "Draft", empty: "Not written" };


/* Grip, then the name, then the numbers that say how far along a site is. */
export const SITE_COLS = "28px 1.8fr .8fr 1.4fr .6fr 34px";

/* ── The landing screen: every site she has ─────────────────────────────────
   This used to be every PAGE she had, and her read of it on 2026-09-15 is why
   it is not any more: "should that table actually be all the sites, because
   when you get into the pages it has the list on the left anyway."

   She was right, and the numbers were worse than she thought. The rail beside
   a page already carried the title, the section count, the words and the
   written/not-written state — four of the six things this table showed. The
   two it added were a last-updated column and a bin, and the bin is also in
   the page's own footer. A whole screen for one column.

   WHAT IS LOST, said plainly because it is a real loss: last-updated per page.
   Nothing else. It has no home in the rail (250px, and a date is the least
   useful thing that could go in it) and it is not worth a screen.

   The site switcher is NOT in the bar here. It is the one screen where it would
   be a dropdown listing the same rows that are already on screen underneath
   it — two answers to "what sites do I have" a thumb apart. */
export function SiteTable({
  sites = [], pagesOf, sectionsFor, loading, error, onDismissError,
  onOpen, onDelete, onAdd, onReorder, menuOpen, setMenuOpen, addOptions = [],
}) {
  return (
    <>
      {/* THE TEXT STAYS VISIBLE HERE, and only here. Her call, 2026-09-15:
          "keep the full text on the table and move it into the information
          tooltip when we drill into the page." Which is the right split — the
          landing screen is where someone arrives and needs orienting; the record
          screen is where she is working and does not. Same words either way, one
          place each, so no crumb hint on this screen and no paragraph on that
          one.

          It also fixes the cramping she reported by putting something back
          rather than by adding empty space. */}
      <Bread text="Build" />
      <h2 style={pTitle}>Sites</h2>
      <p style={pDesc}>{SITE_HINT}</p>

      {/* No view toggle: the thing this screen switches to is one site, and she
          picks WHICH by opening a row. See MomentumBar. */}
      <MomentumBar add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add site" onClick={() => setMenuOpen(!menuOpen)} />
          {menuOpen && (
            /* The fourth element is DISABLED, not enabled — it greys the row
               and prints a "Soon" pill beside it. Both kinds shipped greyed out
               because this read as "is it available". Every kind offered here
               is buildable today; nothing in this menu is ever Soon. */
            <Menu2 head="What kind?" items={addOptions.map((o) => [
              o.id === "landing" ? "🎯" : "🌐", o.label, o.hint, false,
              () => { setMenuOpen(false); onAdd?.(o.id); },
            ])} />
          )}
        </div>
      } />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your sites didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}

      <L1Table
        records={sites} cols={SITE_COLS} labels={["Site", "Kind", "Pages", "Words", ""]}
        loading={loading} loadingText="Loading your sites…"
        empty={<>No sites yet. Click <b style={nb}>Add site</b> to start one.</>}
        onOpen={onOpen} onDelete={onDelete} onReorder={onReorder}
        deleteTitle="Delete site"
        rowFor={(site) => {
          const pages = pagesOf?.(site.id) || [];
          const words = pages.reduce((n, pg) => n + pageWords(sectionsFor?.(pg.id) || []), 0);
          return (
            <>
              <div style={{ minWidth: 0 }}>
                {/* THE ROW'S ANCHOR, and it was the same 13.5px as the numbers
                    three columns over — nothing told the eye where to start.
                    Her read: "it's bland right now, your eye doesn't want to
                    read it." The name is what she scans; it gets the weight,
                    and the address underneath steps back rather than competing. */}
                <div style={{ fontSize: 15, fontWeight: 700, color: site.name ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3 }}>
                  {site.name || "Untitled site"}
                </div>
                {/* THE ADDRESS OPENS THE SITE. Her call, 2026-09-18 — she had
                    asked whether it could hide behind a cog on a phone (it is
                    already in the site settings), then: "unless we could
                    actually make a link that highlights next to it and it's
                    clickable to go to the actual site? that would be nice."
                    More useful than hiding it, and it earns the line it costs.

                    stopPropagation, or a tap opens the site AND walks her into
                    the record behind it — the same rule the grip and the bin
                    follow on every row in this app.

                    Only where it IS a link: `siteHref` refuses anything that is
                    a note rather than an address, and that falls back to the
                    plain label it has always been. */}
                {(() => {
                  const href = siteHref(site.domain);
                  if (!href) {
                    return (
                      <div style={{ fontSize: 11.5, color: site.domain ? B.dim : B.faint, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {site.domain || "No address yet"}
                      </div>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={`Open ${site.domain} in a new tab`}
                      style={{ fontSize: 11.5, color: B.teal, textDecoration: "none", marginTop: 3, display: "inline-flex", alignItems: "center", gap: 4, maxWidth: "100%" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{site.domain}</span>
                      <ExternalLink size={11} style={{ flexShrink: 0 }} />
                    </a>
                  );
                })()}
              </div>
              <div><Badge cls="neutral" text={siteKindDef(site).label} /></div>
              {/* A dot per page — grey nothing, amber written, teal she marked it
                  Done — and the count beside them. A plain "3" said the site had
                  three pages and nothing about how far along it was, which is
                  the question this screen exists to answer. */}
              <StateDots noun="page"
                states={pages.map((pg) => pageState(pg, sectionsFor?.(pg.id) || []))}
                titles={pages.map((pg) => `${pg.title || "Untitled page"} — ${PAGE_STATE_LABEL[pageState(pg, sectionsFor?.(pg.id) || [])]}`)} />
              <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>
                {words || "—"}
              </div>
            </>
          );
        }} />

      <Note><b style={nb}>Open a site to write its pages.</b> Drag by the grip to put them in your order.</Note>
    </>
  );
}


export function WebsitePage({
  draft, setDraft, pages = [], outputs = {}, onSave, onDelete, onBackToTable, backLabel = "All pages", go, isMobile, saving, embedded,
  sections = [], sectionsFor, sectionsLoading, sectionsError, onDismissSectionsError, chatContext = {},
  onSwitchPage,
  onSaveSection, onDeleteSection, onAddSection, onMoveSection, onReorderSections, onRefreshSections, onChangeKind,
  onSparkleSection, sparklingSectionId, draftedSection, draftError, onDismissDraftError,
  draftingId, setDraftingId, offers = [],
  /* So the page can fall back to its SITE's offer when it has none of its own
     (utils/pageOffer) — the site cog is where she links it. */
  sites = [],
  /* Whether the drafting screen opens on the WHOLE page. Held with
     `draftingId`, a level up, so moving page keeps her in the scope she is in. */
  draftingWhole = false, setDraftingWhole = () => {},
  /* (page, styleId) => Promise<boolean>. Absent hides the style picker and the
     GHL prompt — admin-only while new (MomentumHub). */
  onSaveStyle = null,
}) {
  /* Which section she's writing with Brill is held by ID and looked up in the
     CURRENT list, so deleting that section drops the drafting screen on its own
     rather than leaving it open on a row that moved out from under it. The id
     itself lives a level up, because the page list has to hide while she writes. */
  const [kindMenu, setKindMenu] = useState(false);
  /* The address editor is closed by default — see the row below.

     UP HERE, ABOVE THE EARLY RETURN, AND IT HAS TO STAY HERE. It sat below the
     `if (!draft)` line from the day this file was split out (db7443b), which
     meant this component ran TWO hooks with a page open and ONE without — and
     React counts them. It never fired while the only way to have no page was to
     leave the panel, which unmounts the component; the site switcher clears the
     open page WITHOUT unmounting, so the next render had a hook missing and the
     whole screen went down with "Rendered fewer hooks than expected".

     Nothing may go above this line that can return. */
  const [addressOpen, setAddressOpen] = useState(false);
  /* A style pick that did not save says so in amber — "Saved" and silence
     look identical (screen rule 1). Also above the early return. */
  const [styleProblem, setStyleProblem] = useState("");
  const drafting = sections.find((s) => s.id === draftingId) || null;

  /* Where "write this page" begins. Shared with the page switcher in the chat
     header, so both land on the same block. */
  const pageStart = startSection(sections);
  if (!draft) return null;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // `source` is the opaque "this page made itself" marker — see websitePages.js.
  // Never say "Accelerator" about it: My Website is a Momentum module.
  const isStandardPage = draft.source === "accelerator";
  // Only after the split has had its chance: an empty list mid-load isn't an
  // empty page, and saying "no blocks yet" while they're arriving reads as loss.
  const noBlocks = !sectionsLoading && sections.length === 0;
  const changed = sourceChangedFor(draft, sections, outputs);
  /* The page's two choices (spec §0): its STYLE, stored on the page, and her
     WORTHI category, read from what she locked in My Offer and never stored
     here — so a change in My Offer shows on every page at once. */
  const style = styleDef(draft.style);
  const worthi = readWorthi(chatContext.acceleratorOutputs?.offer?.["WORTHI Category"]);
  const pickStyle = async (id) => {
    setStyleProblem("");
    const ok = await onSaveStyle(draft, id || null);
    if (!ok) setStyleProblem("Your style didn't save. If this is the first style you've picked, the database needs supabase/0039_page_style.sql run once in Supabase. Otherwise check your connection and try again.");
  };
  /* Checked as she types rather than on save: the address is the one field with
     a rule she can't see, so the moment to say so is while she's in it. */

  /* The page record saves the way its blocks do: on the way out of a field.
     `savePage` normalises the address itself and drops calls that would write
     an unchanged row, so a blur that changed nothing costs nothing. A page with
     no name, or one pointed at an address another page owns, is not written —
     the read-out in the button row says which. */
  const commitPage = () => { if (canSave) onSave(); };
  const taken = slugTaken(draft.slug, pages, draft.id);
  const address = slugify(draft.slug) || slugify(draft.title);
  const canSave = !!String(draft.title || "").trim() && !!address && !taken && !saving;

  /* Writing one section takes over this area rather than opening a third screen.
     A drill-in would put her two levels away from the page she's writing, which
     is exactly what consolidating the page editor just undid.

     KEYED BY THE SECTION, and it has to stay keyed. The drafter holds that
     section's copy in state from the moment it mounts, so re-rendering the same
     instance against a different section left the PREVIOUS section's words in
     the new section's fields — and the blur autosave then wrote them in as
     though she had typed them (spec 5.4). The pager and the page picker both
     move `drafting` underneath it, so both hit it. The Course call site in
     MomentumHub has always been keyed for the same reason.
     Pinned by momentum/drafterKeys.test.js. */
  if (drafting) {
    return (
      <SectionDrafter key={`${drafting.id}:${draftingWhole ? "whole" : "one"}`} page={draft} section={drafting} chatContext={chatContext} sections={sections}
        usedAs={{ type: "page", id: draft.id, label: draft.title || "Untitled page" }}
        startScope={draftingWhole ? "record" : "block"}
        /* Every section's extras at once, for the whole page. */
        pageScope={sections.reduce((acc, s) => {
          for (const [id, names] of Object.entries(contextScopeFor(draft, s) || {})) {
            acc[id] = [...new Set([...(acc[id] || []), ...(names || [])])];
          }
          return acc;
        }, {})}
        /* An About page is written out of her founder story, and so is an
           "About me" block wherever it sits — including on the homepage, which
           is her correction. Decided here rather than in sections.jsx, which
           does not know what an About page is. */
        extraScope={contextScopeFor(draft, drafting)}
        /* The offer this page serves — set in the page settings cog, and until
           now never sent. The chip names it, which is the uncovering she
           asked for: "linked offer is under a settings which is fine but it
           would be good to uncover it." */
        offer={offerForPage(draft, sites, offers)}
        /* Sites has no text picker — its `picker` slot is the real page
           dropdown — so this is the line the other four already had. */
        where={draft?.title || "Untitled page"}
        /* The bar above carries the way back and the page picker now, so this
           screen draws neither — see `lead` in WebsitePageCopy. */
        chromeInBar
        onGoToSection={(id) => { setDraftingWhole(false); setDraftingId(id); }}
        nextRecord={(() => {
          const i = pages.findIndex((p) => p.id === draft.id);
          const next = i >= 0 ? pages[i + 1] : null;
          return next && onSwitchPage ? { label: next.title || "Untitled page", noun: "page", go: () => onSwitchPage(next) } : null;
        })()}
        onSave={onSaveSection} onBack={() => setDraftingId(null)} />
    );
  }

  return (
    <>
      {/* Page copy renders its own heading and the page sidebar, so the drill-in
          chrome would be a second title and a Back link to the view she's in. */}
      {!embedded && <Back onClick={() => go("website")} label="Back to Website" />}
      {!embedded && <Bread text={`Build · Website · ${draft.isNew ? "New page" : "Edit page"}`} />}
      {!embedded && <h2 style={pTitle}>{draft.title || "New page"}</h2>}

      {/* ── The page, named ──────────────────────────────────────────────────
          Its name IS the heading of this screen, so it's typed where a heading
          would sit rather than filed in a settings card below one. The address
          rides alongside it, because it's the name's address — as two stacked
          form rows they read as two unrelated settings.

          The KIND is not here. It's what the page is rather than something she
          sets, it never changes once the page exists, and a field she can't
          act on is a field in the way. It still decides which locked copy the
          page draws on — see PAGE_KINDS in utils/websitePages.js. */}
      <div style={{ display: "flex", gap: isMobile ? 8 : 14, alignItems: "center", flexWrap: "wrap" }}>
        <PageTitleField value={draft.title || ""} onChange={(v) => set("title", v)} onCommit={commitPage} />
        {/* WHICH RUNG THIS SERVES, read off the offer it points at and never
            stored here (utils/offerRung). The "asset" wording: a thing she
            MAKES gets the ladder's short word — Trust, Core, Continuation —
            where Outreach says Client, because a sales page is read by
            strangers, not clients. No offer connected, no badge. */}
        {(() => {
          const rung = rungTag(draft, offers, "asset");
          return rung ? <Badge cls={rung.badge} text={rung.label} /> : null;
        })()}
        {/* THE PAGE'S STYLE, said where she can see it, and set behind the cog
            with the address and the offer (rule 8: set once, rarely changed). */}
        {onSaveStyle && !draft.isNew && (
          <button type="button" onClick={() => setAddressOpen(true)} title={style ? style.words.join(" · ") : "Pick how this page looks"}
            style={{ ...btnGhost, padding: "5px 11px", fontSize: 12, color: style ? B.mid : B.teal, borderColor: style ? B.border2 : B.tealLine }}>
            {style ? style.label : "Pick a style"}
          </button>
        )}
        {/* DONE IS MARKED HERE, ON THE PAGE — her call, 2026-09-15: "I'd like to
            be able to mark the whole page done, not each section." It sits with
            the name because the name is what it is a statement about.

            An EMPTY page gets no toggle. Marking a page with no words Done is a
            claim about nothing, and rule 11 is that done is counted, not
            claimed — the same reason an empty module keeps a plain read-out. */}
        {!draft.isNew && pageWords(sections) > 0 && (
          <SectionStatus value={draft.status === SECTION_DONE ? SECTION_DONE : "draft"}
            /* `onSave` takes the status directly. Going through `commitPage`
               would write the draft as it stood BEFORE this keystroke — setDraft
               is not synchronous — and the mark would land one press late. */
            onChange={(status) => { set("status", status); if (canSave) onSave(status); }} />
        )}
        {/* The address, read-only until she asks for it. It was a second input
            sitting beside the name, which made naming a page look like filling
            in a form when nearly every page keeps the address it was given. */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <span style={{ fontSize: 13.5, color: taken ? B.salmon : B.faint, whiteSpace: "nowrap" }}>/{address || "…"}</span>
          <IconBtn icon={Settings} label={addressOpen ? "Hide the address" : "Edit the address"}
            onClick={() => setAddressOpen((v) => !v)} />
        </div>
      </div>

      {/* A clash is the one thing she has to see whether or not the editor is
          open — it stops the page saving, and the closed row would just show an
          address that looks fine. */}
      {taken && (
        <div style={{ marginBottom: 4 }}>
          <Hint><span style={{ color: B.salmon }}>Another page already uses /{slugify(draft.slug)}. Two pages can't share an address.</span></Hint>
        </div>
      )}

      {addressOpen && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, background: B.surface, padding: "12px 14px", marginTop: 8, marginBottom: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, maxWidth: 420 }}>
            <span style={{ fontSize: 14, color: B.faint, flexShrink: 0 }}>/</span>
            <input value={draft.slug || ""} onChange={(e) => set("slug", e.target.value)}
              onBlur={() => { set("slug", slugify(draft.slug)); commitPage(); }} autoFocus
              placeholder={slugify(draft.title) || "address"} aria-label="Address on your site"
              style={{ ...input, flex: 1, minWidth: 0, fontSize: 13 }} />
          </div>
          <div style={{ marginTop: 2 }}>
            <Hint>The address on your own site. Leave it blank and it follows the page name: <b style={nb}>/{address || "…"}</b>{isStandardPage ? " — renaming the page never disconnects it from what Brill writes in My Website." : ""}</Hint>
          </div>
          {/* STYLE AND WORTHI (spec §0). The style is a choice, so it saves as it
              is made. The WORTHI line is read-only: it is what she locked in My
              Offer, and that is where it changes. */}
          {!draft.isNew && onSaveStyle && (
            <div style={{ marginTop: 12, maxWidth: 520 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700, flexShrink: 0 }}>Style</span>
                <select value={draft.style || ""} aria-label="Page style" onChange={(e) => pickStyle(e.target.value)}
                  style={{ ...input, flex: 1, minWidth: 0, fontSize: 13 }}>
                  <option value="">No style (the word counts stay as they are)</option>
                  {SITE_STYLES.map((s) => <option key={s.id} value={s.id}>{s.label} · {s.words.join(", ")}</option>)}
                </select>
              </div>
              <Hint>{style
                ? <>Sets how much room each section's words have, and how the page is described to your site builder.</>
                : <>Pick one and every section gets the room that style gives it. Your own word counts still win.</>}</Hint>
              {styleProblem && <div style={{ fontSize: 12.5, color: B.amber, marginTop: 6, lineHeight: 1.5 }}>{styleProblem}</div>}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700, flexShrink: 0 }}>WORTHI category</span>
                <span style={{ fontSize: 13, color: worthi ? B.text : B.dim }}>{worthi ? worthiLabel(worthi) : "Not locked yet. It's set in My Offer."}</span>
              </div>
            </div>
          )}

          {/* THE CONNECTOR (locked 2026-08-22): which offer this page is for. A
              choice, so it saves as it is made. It is what lists the page under
              that offer's "Supported by". */}
          {!draft.isNew && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, maxWidth: 420, marginTop: 12 }}>
              <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700, flexShrink: 0 }}>Offer it serves</span>
              <select value={draft.offerId || ""} aria-label="Offer this page serves"
                onChange={(e) => { const v = e.target.value || null; set("offerId", v); if (canSave) onSave({ offerId: v }); }}
                style={{ ...input, flex: 1, minWidth: 0, fontSize: 13 }}>
                {/* Blank is not "no offer" when the SITE has one: Brill writes this
                    page for the site's offer (utils/pageOffer), so the option says so. */}
                <option value="">{(() => { const so = offerForPage({ siteId: draft.siteId }, sites, offers); return so ? `Same as the site (${so.name || "Untitled offer"})` : "No offer"; })()}</option>
                {offers.map((o) => <option key={o.id} value={o.id}>{(o.name || "Untitled offer") + (o.status === "retired" ? " (retired)" : "")}</option>)}
              </select>
            </div>
          )}

          {/* WHAT A SEARCH RESULT SAYS. Required by a site builder and held
              nowhere until now; the brief has been deriving them from the page
              name and the banner's subhead. Left blank they still are — these
              are overrides, not another two boxes to fill on every page.
              Behind the cog because almost nobody changes them (rule 8), and
              the counts are advice rather than limits: nothing truncates and
              nothing blocks a save, the brief trims on a word if she runs
              long. */}
          {!draft.isNew && (
            <div style={{ marginTop: 14, borderTop: `1px solid ${B.border}`, paddingTop: 12, maxWidth: 520 }}>
              <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>In search results</span>

              <div style={{ marginTop: 8 }}>
                <input value={draft.seoTitle || ""} onChange={(e) => set("seoTitle", e.target.value)}
                  onBlur={commitPage} aria-label="Title in search results"
                  placeholder={draft.title ? `${draft.title} — your business` : "The page's title"}
                  style={{ ...input, fontSize: 13 }} />
                <Hint>Title{draft.seoTitle ? ` — ${draft.seoTitle.length} characters, aim for 60 or fewer` : ", if the page name is not what you want shown"}</Hint>
              </div>

              <div style={{ marginTop: 10 }}>
                <textarea value={draft.seoDescription || ""} onChange={(e) => set("seoDescription", e.target.value)}
                  onBlur={commitPage} aria-label="Description in search results"
                  placeholder="The line that makes someone click"
                  style={{ ...input, fontSize: 13, minHeight: 60, resize: "vertical", display: "block" }} />
                <Hint>Description{draft.seoDescription ? ` — ${draft.seoDescription.length} characters, aim for 160 or fewer` : ", if your banner's subhead is not the right one"}</Hint>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── The page's copy ──────────────────────────────────────────────────
          Blocks, not one wall of text. Each saves on its own the moment she
          leaves it — a page with six blocks and one Save button at the bottom
          is six edits she can lose in one misclick.

          No heading over them. "Page copy" named the screen she was already on,
          under a title that already said which page it was, and "each block
          saves as you leave it" explained a behaviour she has watched work
          every time. Both earned their line once and then just sat there. */}
      <div style={{ height: 22 }} />

      {/* A draft that didn't land is not a save that failed, so it gets its own
          line and its own words. Nothing was written either way. */}
      {draftError && (
        <div onClick={onDismissDraftError} style={{ background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 9, padding: "11px 14px", marginBottom: 14, fontSize: 13, color: B.text, cursor: "pointer" }}>
          <b>Brill didn't write that one.</b> {draftError}
        </div>
      )}

      {sectionsError && (
        <div onClick={onDismissSectionsError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 14, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>That block didn't save.</b> {sectionsError} Your words are still on screen — try again once you're back online.
        </div>
      )}

      {/* The offer ladder's Resync, in its website form: she re-locked My Website,
          so these blocks are behind. Opt-in, because it overwrites her edits. */}
      {changed && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", background: B.amberQuiet, border: `1px solid ${B.amberLine}`, borderRadius: 10, padding: "11px 14px", marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, color: B.text }}>
            <b>My Website has been rewritten.</b> These sections were made from an older version of it.
          </span>
          <button onClick={() => onRefreshSections(draft)} style={{ ...btnGhost, padding: "7px 14px", fontSize: 12 }}>Replace with the new copy</button>
        </div>
      )}

      {sectionsLoading && sections.length === 0 ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 26, textAlign: "center", color: B.dim, fontSize: 13 }}>
          Loading this page's copy…
        </div>
      ) : noBlocks ? (
        <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 26, textAlign: "center", color: B.dim, fontSize: 13, lineHeight: 1.6 }}>
          {draft.isNew
            ? <>Save the page first — then its sections live here.</>
            : sourceSectionFor(draft)
              ? <>This page isn't written yet. Click <b style={nb}>Add section</b> and pick what it needs — Brill will write any of them with you.</>
              : <>A page you added yourself. Click <b style={nb}>Add section</b> and pick what it needs.</>}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SortableList ids={sections.map((s) => s.id)} onReorder={(from, to) => onReorderSections(draft.id, from, to)}>
            {sections.map((s) => (
              <SortableItem key={s.id} id={s.id}>
                {({ handle, dragging }) => (
                  <SectionBlock
                    /* KEYED BY THE DRAFT STAMP, and it has to stay keyed. A
                       block holds its own copy of `content` from the moment it
                       mounts, so a sparkle wrote the row, moved the word count
                       in the rail, and left the row itself reading "Nothing here
                       yet" until she opened the chat and came back — which
                       remounted it. Same mechanism Course uses. */
                    key={`${s.id}:${draftedSection?.sectionId === s.id ? draftedSection.n : 0}`}
                    startOpen={draftedSection?.sectionId === s.id}
                    section={s} dragHandle={handle} dragging={dragging}
                    pageStyle={draft.style}
                    usedAs={{ type: "page", id: draft.id, label: draft.title || "Untitled page" }}
                    onSave={onSaveSection} onDelete={onDeleteSection}
                    onChangeKind={onChangeKind} onDraft={(sec) => { setDraftingWhole(false); setDraftingId(sec.id); }}
                    /* The sparkle drafts in place and never leaves this screen;
                       the chat icon beside it is the one that opens the
                       conversation. They used to be the same call. */
                    onSparkle={(sec) => onSparkleSection?.(draft, sec)}
                    sparkling={sparklingSectionId === s.id} />
                )}
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}

      {!draft.isNew && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
          <AddSectionMenu open={kindMenu} setOpen={setKindMenu} onAdd={(kind) => onAddSection(draft.id, kind)} />
          {/* Writing the page starts in Momentum's own chat, on the banner.
              This used to LEAVE the studio for the Accelerator's My Website
              module, which is the wrong window: it writes the locked source
              prose, not these blocks, so she came back to a page that hadn't
              changed. Starting on the banner because that is the top of the
              page and the block she'd write first.

              It opens on the WHOLE page (2026-09-16), with Section one
              press away; the section it sits on is the banner, as before. */}
          {pageStart && (
            <button onClick={() => { setDraftingWhole(true); setDraftingId(pageStart.id); }} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
              Write this page with Brill
            </button>
          )}
        </div>
      )}

      {/* THE PAGE, FOR WHOEVER BUILDS IT. Under the sections rather than in the
          bar, because it is the last thing she does with a page and not one of
          the things she does TO it. A website here is a handoff doc (her call,
          2026-08-16) — this is the handoff. */}
      <CopySiteBrief page={draft} sections={sections} outputs={chatContext.acceleratorOutputs || {}}
        ghl={!!onSaveStyle} worthi={worthi}
        /* WHO THE SITE IS ABOUT. `displayName` is the only name settings holds
           (App.jsx), so the business name is not a field anywhere yet — it
           comes back in `missing` rather than being invented, which is the
           whole rule of this thing. A business-name field is a small change
           and hers to want. */
        identity={{
          businessName: chatContext.userSettings?.businessName || "",
          ownerName: chatContext.userSettings?.displayName || "",
          ownerPhotoUrl: chatContext.userSettings?.photoUrl || "",
          logoUrl: chatContext.userSettings?.logoUrl || "",
          iconUrl: chatContext.userSettings?.brand?.logos?.icon || "",
          audience: chatContext.userSettings?.audience || "",
          /* Me → Brand's colours, which win over the locked palette. */
          brand: chatContext.userSettings?.brand || null,
        }} />

      {/* ── Nothing to press ────────────────────────────────────────────────
          This row was Save, Save as draft, Cancel and a sentence explaining
          them. All four described a model this app doesn't have: there is no
          site, so nothing publishes and live/draft only changed a word in a
          list; and the blocks — the half of this screen she spends her time in
          — have always saved themselves, which made a Save button a promise
          about the wrong thing.

          So the page saves itself too, on the way out of the name and the
          address, and this row just says so. Cancel goes with it: there is no
          pending edit left for it to throw away. Delete moves up into the space
          the sentence was using. */}
      <RecordFooter backLabel={backLabel} onBack={onBackToTable} saving={saving}
        problem={taken ? "Address taken — not saved" : !canSave ? "Give it a name to save it" : ""}
        onDelete={draft.isNew ? null : onDelete} />
    </>
  );
}


export function WebsitePageCopy({
  pages = [], selectedId, onSelect, onReorder, sectionsFor, isMobile, children,
  menuOpen, setMenuOpen, onAddStandard, onAddOther, missing = [], standardPages, level1, drawer, drafting,
  onBackFromDrafting, onSwitchPage, onBackToSites, siteName, onMark,
  /* From `useRecordScreen` in the hub — held there because the page's FOOTER
     back is rendered inside `children` and has to go to the same place as the
     one at the top of the screen. */
  screen,
}) {
  const page = pages.find((p) => p.id === selectedId) || pages[0] || null;
  /* On a phone the way back belongs to whichever LEVEL is on screen, and
     RecordCopy is the only thing that knows which that is — so it renders it
     (via onExit / its own "All pages") and the bar stands down. While drafting
     neither carries it: the drafting screen draws its own, at every width. */
  const twoPane = useTwoPane();
  /* While she is drafting, at ANY width (2026-09-24 — until then a phone
     only): the panel's chrome gives the screen up to the chat, and
     MomentumBar stands down too. The drafting screen's one row is all that
     sits above the conversation. */
  const bare = !!drafting;
  return (
    <>
      {/* BUILD · SITES, then the site's own name big underneath — her shape,
          2026-09-15: "can it just be breadcrumbs at the top, build → sites →
          then my site is big."

          The heading is the RECORD, not the panel, which is why the crumb has to
          carry the panel: rule 9 bans a heading naming the screen she is on, and
          a heading naming which site she is in is the one thing the rail cannot
          say. The three-line description that used to sit under it is in the
          hint on the crumb. */}
      {/* THE PANEL'S OWN HEAD STANDS DOWN ON A PHONE WHILE SHE DRAFTS — her
          report, 2026-09-22. The crumb and the heading were two of the rows
          pushing the conversation down to four lines with its input below the
          fold, and neither answers a question she has while writing a block:
          the drafting screen's one row names where she is. */}
      {!bare && (<>
      <Bread text="Build" crumb="Sites" hint={SITE_HINT} />
      <h2 style={pTitleAlone}>{siteName || "Sites"}</h2>
      </>)}

      {/* ONE ROW, in her order: back, which site, its settings, which page, add.
          The drafting screen used to carry its own back-and-picker row under
          this one, so the chat began four rows down. The bar it needed was
          already here; it just was not being offered. */}
      <WebsiteBar drafting={drafting} menuOpen={menuOpen} setMenuOpen={setMenuOpen}
        onAddStandard={onAddStandard} onAddOther={onAddOther} missing={missing}
        standardPages={standardPages} level1={level1} drawer={drawer}
        level2={drafting && (
          /* Which PAGE, after which SITE — her order, and the hierarchy's. */
          <PagePicker pages={pages} page={page} sectionsFor={sectionsFor} onPick={(p) => onSwitchPage?.(p)} />
        )}
        lead={drafting ? (
          <BarBack label="← Back to the page" onClick={onBackFromDrafting} />
        ) : (twoPane && onBackToSites) ? (
          /* THE WAY BACK, AT THE TOP. Her read, 2026-09-15: "it feels like table
             is the first page" — and it is. Table is the list, this is one page
             out of it, drafting is one section out of that. Three levels, and
             until now only the deepest one had a way back in the bar.

             It EXISTED, in the footer at the bottom of RecordFooter — but the
             footer sits under every block on the page, so on a Homepage with
             four sections she scrolls past the whole thing to find it. A way
             out you have to go looking for is not a way out.

             Same slot every time, one at a time: drafting shows back-to-the-
             page, page copy shows back-to-all-pages. Never both — two backs
             side by side and she has to read them to know which is which. */
          <BarBack label="← All sites" onClick={onBackToSites} />
        ) : null} />

      {/* Writing one section is its own two-column screen, so the rail of other
          pages steps out of the way — a third column of things she isn't doing.
          It comes straight back when she's done. That rule, the rail and the
          empty state are all `RecordCopy` now (shell.jsx); what stays here is
          what a PAGE's row reads.

          Reordering moved INTO the rail, by grip, replacing the pair of arrows
          that used to sit on the right of every row (rule 10, spec R5). She
          should never have to go back out to the Table to put one page before
          another, and the arrows could not express "move this to the end". */}
      <RecordCopy
        /* Three levels, so the list screen STAYS — this only says where its own
           way out goes, which on a phone is the bar's job taken over. */
        onExit={onBackToSites} exitLabel="All sites" screen={screen}
        records={pages} selectedId={selectedId} onSelect={onSelect} onReorder={onReorder} onMark={onMark}
      emptyHint={<>No pages yet. Click <b style={nb}>Add page</b> above and pick one.</>}
        noun="page" bare={drafting}
        rowFor={(p) => {
          const blocks = sectionsFor?.(p.id) || [];
          return {
            title: p.title,
            meta: blocks.length ? `${blocks.length} section${blocks.length === 1 ? "" : "s"} · ${pageWords(blocks)} words` : "Empty",
            /* The same green/amber as the dropdown in the drafting chat and the
               dots on the blocks: written, or still to write. Answered here and
               not in the shell — three panels currently answer "is this done"
               three different ways, and picking one for all of them is step 5,
               not a layout change. */
            dot: B[STATUS_DOT[pageState(p, blocks)]],
            dotTitle: PAGE_STATE_LABEL[pageState(p, blocks)],
            /* Pressable once there is something to call finished. An empty page
               keeps a plain dot: marking nothing Done is a claim about nothing,
               the same rule the page's own toggle follows. */
            markable: pageState(p, blocks) !== "empty",
            done: pageState(p, blocks) === "done",
          };
        }}>
        {children}
      </RecordCopy>
      {/* The "Copy only, on purpose" Note that used to close this screen is gone,
          2026-09-15, on her say-so — it said the same thing the hint on the
          breadcrumb now says, one screen further down and to someone already
          three pages into writing. Rule 9: no caption explaining behaviour she
          has already watched work. */}
    </>
  );
}



/* The page rail steps aside on this screen (see WebsitePageCopy), so the page
   she's in — and the way to another one — comes back here as a dropdown. She
   lands on the new page's banner rather than its section list, because leaving
   the chat is what Back is for. */
export function PagePicker({ pages = [], page, sectionsFor, onPick }) {
  const [open, setOpen] = useState(false);
  /* Written or not — see pageState. Live/draft is not the question: there is no
     site for a page to be live on, and what she wants down a list of pages is
     which ones still need words. */
  const label = (p) => {
    const state = pageState(p, sectionsFor?.(p.id) || []);
    if (state === "done") return [PAGE_STATE_LABEL.done, B.teal, B.teal];
    if (state === "draft") return [PAGE_STATE_LABEL.draft, B.amberFill, B.amber];
    return [PAGE_STATE_LABEL.empty, B.ghost, B.faint];
  };
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen((v) => !v)} aria-haspopup="listbox" aria-expanded={open}
        style={{ ...btnGhost, padding: "8px 12px", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
        {page?.title || "Page"}
        <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
      </button>
      {open && (
        <>
          {/* Clicking anywhere else closes it. A transparent sheet rather than a
              document listener, which is what every other menu in here does. */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
          <div role="listbox" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20, minWidth: 210, background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 10, padding: 5, boxShadow: "var(--shadow-menu)" }}>
            {pages.map((p) => {
              const on = p.id === page?.id;
              const [text, dot, ink] = label(p);
              return (
                <div key={p.id} role="option" aria-selected={on}
                  onClick={() => { setOpen(false); if (!on) onPick(p); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "8px 10px", borderRadius: 7, cursor: on ? "default" : "pointer", background: on ? B.tealQuiet : "transparent" }}>
                  <span style={{ fontSize: 13, fontWeight: on ? 700 : 600, color: on ? B.teal : B.text, whiteSpace: "nowrap" }}>{p.title || "Untitled page"}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: ink, flexShrink: 0 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot }} />{text}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


